import { z } from "zod";

/**
 * Formatting a human or a provider may sprinkle through a phone number.
 *
 * Beyond the ASCII punctuation a person types, this strips what a *paste*
 * carries. Copying a number out of Contacts or Messages on Apple platforms
 * wraps it in bidirectional formatting controls and writes its separators as
 * non-breaking hyphens, so `+1 (650) 283-4023` arrives bracketed by U+202D and
 * U+202C and no longer matches `/^\d+$/` once the ASCII-only class has run.
 * Those characters are invisible, so the value looks correct in every log and
 * every UI while silently failing to normalize.
 *
 * The class is deliberately wider than the five characters observed in
 * production: they are arbitrary samples of one phenomenon, and a narrow fix
 * invites the same bug under the next control character. `\s` already covers
 * U+00A0, U+202F and U+FEFF, so what is added here is the zero-width and
 * directional controls (U+200B–U+200F, U+202A–U+202E, U+2066–U+2069) and the
 * non-ASCII dashes (U+2010–U+2015, U+2212).
 *
 * Lives here rather than beside its callers because `handleValue.ts` imports
 * from this file and not the other way around — `api/src/types/` may not import
 * outward. Keeping one definition is what stops the two normalizers disagreeing.
 */
const PHONE_PUNCTUATION = /[\s\-().\u200b-\u200f\u2010-\u2015\u202a-\u202e\u2066-\u2069\u2212]/g;

/**
 * Strip that formatting out of a value.
 *
 * A function rather than the bare regex because the regex is `/g`, and a `/g`
 * regex carries `lastIndex` between calls: shared across files, the first
 * `.test()` anyone adds would start skipping inputs. `.replace` resets it, so
 * keeping the only caller in here keeps that impossible.
 */
export function stripPhonePunctuation(value: string): string {
  return value.replace(PHONE_PUNCTUATION, "");
}

/**
 * Normalizes a phone number by removing only phone formatting characters
 * and adding the US country code if missing. Rejects strings with alphabetical characters.
 */
function normalizePhoneNumber(
  phoneNumber: string,
): { success: true; data: string } | { success: false; error: string } {
  // Check for alphabetical characters (reject strings like "abc123" or "1-800-FLOWERS")
  if (/[a-zA-Z]/.test(phoneNumber)) {
    return { success: false, error: "Phone number cannot contain alphabetical characters" };
  }

  const cleaned = stripPhonePunctuation(phoneNumber);

  // Remove leading + for processing, but remember if it was there
  const hasPlus = cleaned.startsWith("+");
  const digitsOnly = cleaned.replace(/^\+/, "");

  // Must contain only digits after cleaning
  if (!/^\d+$/.test(digitsOnly)) {
    return {
      success: false,
      error: "Phone number must contain only digits and valid formatting characters",
    };
  }

  // If it already starts with +, return as-is (after cleaning)
  if (hasPlus) {
    return { success: true, data: `+${digitsOnly}` };
  }

  // Check if it already has country code (11 digits for US: 1 + 10 digit number)
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    return { success: true, data: `+${digitsOnly}` };
  }

  // Add US country code if missing (for 10-digit US numbers)
  return { success: true, data: `+1${digitsOnly}` };
}

/**
 * Validates and normalizes a phone number to E.164 format
 * Supports common US phone number formats with only numeric digits and standard formatting:
 * - +15551234567 (already E.164)
 * - 15551234567
 * - 5551234567
 * - 555-123-4567
 * - (555) 123-4567
 * - 555.123.4567
 * Rejects alphabetical characters (e.g., "1-800-FLOWERS")
 */
export function validatePhoneNumber(
  input: string,
): { success: true; data: string } | { success: false; error: string } {
  if (!input || typeof input !== "string") {
    return { success: false, error: "Phone number must be a non-empty string" };
  }

  const normalizeResult = normalizePhoneNumber(input);
  if (!normalizeResult.success) {
    return normalizeResult;
  }

  // Validate E.164 format: + followed by country code (1-3 digits) and phone number (up to 15 total digits)
  const e164Regex = /^\+[1-9]\d{1,14}$/;

  if (!e164Regex.test(normalizeResult.data)) {
    return { success: false, error: "Invalid phone number format" };
  }

  return { success: true, data: normalizeResult.data };
}

/**
 * Zod schema for phone number validation that accepts common formats
 * and normalizes to E.164 format
 */
export const phoneNumberSchema = z.string().transform((value, ctx) => {
  const result = validatePhoneNumber(value);
  // `=== true` rather than a truthy check: this file is symlinked into
  // native/src/types/, where the tsconfig is non-strict and won't narrow
  // the discriminated union on an implicit boolean check (`if (result.success)`).
  if (result.success === true) {
    return result.data;
  }
  ctx.addIssue({
    code: "custom",
    message: result.error,
  });
  return z.NEVER;
});
